import { createLinkChain } from 'initializeApolloClient';
import { getActiveAuthSelector, getJwtTokenSelector } from 'selectors/appSelectors';
import { useApolloClient } from '@apollo/client';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { usePrevious } from '@tock/hooks';
import { useSelector, useStore } from 'react-redux';
import React from 'react';

function valueFullyChanged<T>(prevValue: T | undefined, value: T) {
  return value !== undefined && prevValue !== undefined && prevValue !== value;
}

/**
 * This component is responsible for reinitializing the apollo client when the user's login state changes (as represented
 * by app.jwtToken.)
 */
export const ApolloConfigurationStateMonitor: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const apolloClient = useApolloClient();
  // We (bp@, matthew@) think that the configuration state monitor would work if it also
  // used the businessHeaderSelector() and monitored whether this changed. However, the old code
  // within initializeApolloClient is actively asking for store values in a closure, and in the short term
  // the safest thing to do is to continue using that approach. So we call into useStore() here instead.
  const store = useStore();

  // const businessHeaders = useSelector(businessHeaderSelector);

  const jwtToken = useSelector(getJwtTokenSelector);
  const prevJwtToken = usePrevious(jwtToken);

  const activeAuth = useSelector(getActiveAuthSelector);
  const activeBusinessId = activeAuth?.businessId;
  const prevActiveBusinessId = usePrevious(activeBusinessId);
  const activeBusinessGroupId = activeAuth?.businessGroupId;
  const prevActiveBusinessGroupId = usePrevious(activeBusinessGroupId);

  const includeRetryLink = useFeatureFlag('ENABLE_RETRY_LINK_GRAPHQL_SUBSCRIPTIONS');

  React.useEffect(() => {
    if (
      activeBusinessId &&
      activeBusinessGroupId &&
      (valueFullyChanged(prevJwtToken, jwtToken) ||
        valueFullyChanged(prevActiveBusinessId, activeBusinessId) ||
        valueFullyChanged(prevActiveBusinessGroupId, activeBusinessGroupId))
    ) {
      apolloClient.setLink(
        createLinkChain({
          jwtToken,
          activeBusinessId,
          activeBusinessGroupId,
          store,
          clientSelfReference: apolloClient,
          includeRetryLink,
        })
      );
    }
  }, [
    apolloClient,
    jwtToken,
    activeBusinessGroupId,
    prevJwtToken,
    prevActiveBusinessGroupId,
    activeBusinessId,
    prevActiveBusinessId,
    includeRetryLink,
    store,
  ]);

  return null;
};

export default ApolloConfigurationStateMonitor;
